import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { Confirm, Icons, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';

import { useMutateDeleteAssetsFromCart } from '@hooks/assets/useMutateDeleteAssetsFromCart';
import { useMutateSendAssetCartRequest } from '@hooks/assets/useMutateSendAssetCartRequest';
import { useQueryAssetCartRequestList } from '@hooks/assets/useQueryAssetCartRequestList';
import { useQueryAssetCartRequestOptions } from '@hooks/assets/useQueryAssetCartRequestOptions';
import { useQueryAssetSubTypesByType } from '@hooks/assets/useQueryAssetSubTypesByType';

import { assetCartRequestFullView } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setAssetOwnerId,
	setAssetSubTypeId,
	setAssetTypeId,
	setFromDate,
	setIsAvailableInPeriod,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setToDate,
} from '@slices/assetCartRequestFullView';

import { setFilterUtil } from '@utils/filterUtil';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { EAdminPanelPageTitles, EMenuActionsTypes, TMenuActionsTypes } from '@enums/common';

import colors from '@constants/colors';
import { MODAL_WIDTH_580 } from '@constants/modals';
import { periodAvailabilityOptions } from './constants';
import { searchTooltipText } from '@components/Map/AssetCartRequest/constants';
import { noSubTypeOptions } from '@components/routes/AdminPanel/AssetsList/constants';

import EditStartEndDateForm from './EditStartEndDateForm';
import { getColumns } from './manageAssetCartRequestColumns';
import TotalAssetsInformation from './TotalAssetsInformation';

import styles from './AssetCartRequestFullView.module.scss';

const AssetCartRequestFullView = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);

	const [selectedAssetOwnerId, setSelectedAssetOwnerId] = useState<TSelectOption | null>(null);
	const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<TSelectOption | null>(null);
	const [selectedAssetSubTypeId, setSelectedAssetSubTypeId] = useState<TSelectOption | null>(null);
	const [selectedPeriodAvailability, setSelectedPeriodAvailability] = useState<TSelectOption | null>(null);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);

	const [isEditStartEndDateForm, setIsEditStartEndDateForm] = useState<boolean>(false);
	const [assetIdForEdit, setAssetIdForEdit] = useState<string | null>(null);
	const [assetDbIdForEdit, setAssetDbIdForEdit] = useState<string | null>(null);
	const [isEditStartEndDateFormDirty, setIsEditStartEndDateFormDirty] = useState<boolean>(false);
	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);

	const openEditStartEndDateForm = useCallback((assetId: string, assetDbId: string) => {
		setAssetIdForEdit(assetId);
		setAssetDbIdForEdit(assetDbId);

		setIsEditStartEndDateForm(true);
	}, []);

	const closeEditStartEndDateForm = () => {
		setAssetIdForEdit(null);
		setAssetDbIdForEdit(null);

		setIsEditStartEndDateForm(false);
	};

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		assetOwnerId,
		assetTypeId,
		assetSubTypeId,
		isAvailableInPeriod,
		fromDate,
		toDate,
	} = useAppSelector(assetCartRequestFullView);

	const {
		countOfRecords,
		isLoadingAssetCartRequest: isLoadingAssetCartRequestFullView,
		assetCartRequestList,
		availableAssetRequestCount,
		unavailableAssetRequestCount,
		assetOwners,
		numberOfFutureRequests,
	} = useQueryAssetCartRequestList();

	const {
		handleSendAssetCartRequest,
		isLoadingSendAssetCartRequest,
	} = useMutateSendAssetCartRequest();

	const closeDeleteConfirm = () => {
		setIsDeleteConfirm(false);
		setSingleIdForRemove(null);
	};

	const {
		handleDeleteAssets,
		isLoadingDeleteAssets,
	} = useMutateDeleteAssetsFromCart({
		closeDeleteConfirm,
	});

	const isLoadingAssetCartRequest = (
		isLoadingDeleteAssets ||
		isLoadingSendAssetCartRequest
	);

	const {
		ownersOptions,
		assetTypesOptions,
	} = useQueryAssetCartRequestOptions();

	const { assetSubTypesByType } = useQueryAssetSubTypesByType(assetTypeId);

	const assetCartRequestTableStyles = cn(styles.assetCartRequestTableContainer, {
		[styles.withTotalAssetsInformation]: availableAssetRequestCount || unavailableAssetRequestCount,
	});

	const openDeleteConfirm = () => setIsDeleteConfirm(prevState => !prevState);

	const columns = useMemo(() => getColumns({
		navigate,
		openEditStartEndDateForm,
		setSingleIdForRemove,
		openDeleteConfirm,
	}), [navigate, openEditStartEndDateForm]);

	const startDate = useMemo(() => fromDate ? new Date(fromDate) : null, [fromDate]);
	const endDate = useMemo(() => toDate ? new Date(toDate) : null, [toDate]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const onChangeFromDate = useCallback((value: number | null) =>
		dispatch(setFromDate({ fromDate: value })), [dispatch]);

	const onChangeToDate = useCallback((value: number | null) => dispatch(setToDate({ toDate: value })), [dispatch]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	}, []);

	const deleteItemsHandler = useCallback(() => {
		if (singleIdForRemove) return handleDeleteAssets({ ids: [singleIdForRemove] });

		return handleDeleteAssets({ ids: checkedIds, actionType: actionId });
	}, [
		actionId,
		checkedIds,
		handleDeleteAssets,
		singleIdForRemove,
	]);

	const deleteAllItemsHandler = useCallback(() => {
		handleDeleteAssets({ ids: [], actionType: EMenuActionsTypes.all });
	}, [handleDeleteAssets]);

	const assetSubTypesForFilter = useMemo(() => {
		return assetSubTypesByType?.length ? [...assetSubTypesByType, noSubTypeOptions] : [];
	}, [assetSubTypesByType]);

	const filters: Array<TTableFilter> = useMemo(() => [
		{
			name: 'assetOwnerId',
			placeholder: 'Asset Owner',
			option: selectedAssetOwnerId,
			options: ownersOptions,
			width: '200px',
			setState: setSelectedAssetOwnerId,
			defaultValue: assetOwnerId,
		},
		{
			name: 'assetType',
			placeholder: 'Asset type',
			option: selectedAssetTypeId,
			options: assetTypesOptions,
			width: '200px',
			setState: setSelectedAssetTypeId,
			defaultValue: assetTypeId,
		},
		{
			name: 'assetSubType',
			placeholder: 'Asset subtype',
			option: selectedAssetSubTypeId,
			options: assetSubTypesForFilter,
			width: '200px',
			setState: setSelectedAssetSubTypeId,
			defaultValue: assetSubTypeId,
		},
		{
			name: 'isAvailableInPeriod',
			placeholder: 'Period availability',
			option: selectedPeriodAvailability,
			options: periodAvailabilityOptions,
			width: '200px',
			setState: setSelectedPeriodAvailability,
			defaultValue: isAvailableInPeriod,
		},
	], [
		assetOwnerId,
		assetSubTypeId,
		assetSubTypesForFilter,
		assetTypeId,
		assetTypesOptions,
		isAvailableInPeriod,
		ownersOptions,
		selectedAssetOwnerId,
		selectedAssetSubTypeId,
		selectedAssetTypeId,
		selectedPeriodAvailability,
	]);

	const dateFilters: Array<ITableDatepickerFilter> = useMemo(() => [
		{
			name: 'from',
			label: 'From',
			handleChange: onChangeFromDate,
			selected: startDate,
		},
		{
			name: 'to',
			label: 'To',
			handleChange: onChangeToDate,
			selected: endDate,
		},
	], [
		endDate,
		startDate,
		onChangeFromDate,
		onChangeToDate,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetOwnerId,
			currentValue: assetOwnerId,
			setAction: setAssetOwnerId,
			fieldName: 'assetOwnerId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetOwnerId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetTypeId,
			currentValue: assetTypeId,
			setAction: setAssetTypeId,
			fieldName: 'assetTypeId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetTypeId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetSubTypeId,
			currentValue: assetSubTypeId,
			setAction: setAssetSubTypeId,
			fieldName: 'assetSubTypeId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetSubTypeId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedPeriodAvailability,
			currentValue: isAvailableInPeriod,
			setAction: setIsAvailableInPeriod,
			fieldName: 'isAvailableInPeriod',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPeriodAvailability]);

	return (
		<div className={styles.assetCartRequestContainer}>
			<PageTitle
				icon={Icons.fullView({ color: colors.white })}
				title={EAdminPanelPageTitles.RequestList}
			/>
			{isDeleteConfirm &&
				<Modal
					isOpen={isDeleteConfirm}
					onClose={closeDeleteConfirm}
					title='Confirm action'
					width='480px'
				>
					<Confirm
						isLoading={isLoadingDeleteAssets}
						onClose={closeDeleteConfirm}
						onSubmit={deleteItemsHandler}
						question={`Are you sure you want to delete the asset${singleIdForRemove ? '' : 's'}?`}
					/>
				</Modal>
			}
			<div className={styles.pageContainer}>
				<div className={assetCartRequestTableStyles}>
					<Table
						columns={columns}
						data={assetCartRequestList}
						dateFilters={dateFilters}
						emptyText='Your Shopping Request list is empty'
						filters={filters}
						handleCustomDelete={handleCustomDelete}
						isEmptyTextCenter
						isLoading={isLoadingAssetCartRequestFullView}
						isOpenFilters={!!(assetOwnerId || assetTypeId || fromDate || toDate)}
						isOpenSearch={!!searchValue.length}
						isPinColumns
						pagination={pagination}
						search={search}
						searchThreshold={0}
						setCheckedIds={setCheckedIds}
						sort={sort}
						stickyLeftColumnIndexes={[0, 1]}
					/>
				</div>
				{!!(availableAssetRequestCount || unavailableAssetRequestCount)
					&& <TotalAssetsInformation
						assetOwners={assetOwners}
						availableAssetRequestCount={availableAssetRequestCount}
						handleClearAssetCartRequest={deleteAllItemsHandler}
						handleSendAssetCartRequest={handleSendAssetCartRequest}
						isLoading={isLoadingAssetCartRequest}
						numberOfFutureRequests={numberOfFutureRequests}
						unavailableAssetRequestCount={unavailableAssetRequestCount}
					/>
				}
			</div>
			{assetIdForEdit && assetDbIdForEdit && (
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isEditStartEndDateFormDirty}
					isOpen={isEditStartEndDateForm}
					onClose={closeEditStartEndDateForm}
					title='Edit terms of requested asset'
					width={MODAL_WIDTH_580}
				>
					<EditStartEndDateForm
						assetDbIdForEdit={assetDbIdForEdit}
						assetIdForEdit={assetIdForEdit}
						closeModal={closeEditStartEndDateForm}
						setIsDirtyForm={setIsEditStartEndDateFormDirty}
					/>
				</Modal>
			)}
		</div>
	);
};

export default memo(AssetCartRequestFullView);
